import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { DATE_FORMATS } from "../../constants";

dayjs.extend(customParseFormat);

/**
 * True only on live production — applies IST via `DATE_FORMATS.formatDateTime`.
 * UAT/QA/dev builds use `NODE_ENV=production` for React; do not treat that as prod.
 */
export function isRefundModuleProductionEnv(): boolean {
  const env = (process.env.REACT_APP_ENV || "").trim().toLowerCase();
  if (env === "uat" || env === "qa" || env === "dev" || env === "development") {
    return false;
  }

  const apiBaseUrl = (process.env.REACT_APP_API_BASE_URL || "").toLowerCase();
  if (
    apiBaseUrl.includes("samyakinfo.com") ||
    apiBaseUrl.includes("motovoltev") ||
    /uat|qa|staging|dev/.test(apiBaseUrl)
  ) {
    return false;
  }

  if (env === "production") {
    return true;
  }

  return (
    apiBaseUrl.includes("services.ridenimbo.com") ||
    apiBaseUrl.includes("ridenimbo.com")
  );
}

/** Same pattern as `DATE_FORMATS.formatDateTime` — e.g. `Apr 30 2026, 08:19 PM`. */
function formatWallClockDateTime(date: Date): string {
  const month = date.toLocaleString("en-US", { month: "short" });
  const day = date.getDate();
  const year = date.getFullYear();
  const hours = date.getHours();
  const minutes = date.getMinutes();
  const ampm = hours >= 12 ? "PM" : "AM";
  const displayHours = (hours % 12 || 12).toString().padStart(2, "0");
  const displayMinutes = minutes.toString().padStart(2, "0");
  return `${month} ${day} ${year}, ${displayHours}:${displayMinutes} ${ampm}`;
}

/**
 * Parses API timestamps without shifting wall-clock (QA/UAT/dev).
 * ISO strings without `Z` use date/time digits from the string as-is.
 */
function parseRefundTimestampPreserveWallClock(raw: string): Date | null {
  const s = raw.trim();
  const isoMatch = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2}):(\d{2})/.exec(s);
  if (isoMatch) {
    const [, y, mo, d, h, mi, sec] = isoMatch;
    const parsed = dayjs(
      `${y}-${mo}-${d} ${h}:${mi}:${sec}`,
      "YYYY-MM-DD HH:mm:ss",
      true,
    );
    if (parsed.isValid()) return parsed.toDate();
  }
  if (/^\d{2}\/\d{2}\/\d{4} \d{2}:\d{2}:\d{2}$/.test(s)) {
    const parsed = dayjs(s, "MM/DD/YYYY HH:mm:ss", true);
    if (parsed.isValid()) return parsed.toDate();
  }
  const parsed = dayjs(s);
  if (parsed.isValid()) return parsed.toDate();
  try {
    const fallback = new Date(s);
    return Number.isNaN(fallback.getTime()) ? null : fallback;
  } catch {
    return null;
  }
}

/**
 * Refund-module date/time display (`Apr 30 2026, 08:19 PM`).
 * Production: IST adjustment via `DATE_FORMATS.formatDateTime`.
 * QA / UAT / dev: same layout, wall-clock from API (no +5:30).
 */
export function formatRefundDateTime(
  dateString: string | null | undefined,
): string {
  const raw = dateString != null ? String(dateString).trim() : "";
  if (!raw) return "";

  if (isRefundModuleProductionEnv()) {
    return DATE_FORMATS.formatDateTime(raw);
  }

  const date = parseRefundTimestampPreserveWallClock(raw);
  if (!date) return raw;
  return formatWallClockDateTime(date);
}

/** API field names for ride / trip start (table, export, filters). */
export const REFUND_RIDE_START_DATE_KEYS = [
  "tripstartdate",
  "tripStartDate",
  "TripStartDate",
  "ridestartdate",
  "rideStartDate",
  "RideStartDate",
  "bookingstartdate",
  "bookingStartDate",
  "planstartdate",
  "planStartDate",
] as const;

/** API field names for ride end / completion (table, export, filters). */
export const REFUND_RIDE_END_DATE_KEYS = [
  "ridecompletedate",
  "rideCompleteDate",
  "RideCompleteDate",
  "tripenddate",
  "tripEndDate",
  "TripEndDate",
  "rideenddate",
  "rideEndDate",
  "RideEndDate",
  "bookingenddate",
  "bookingEndDate",
  "planenddate",
  "planEndDate",
] as const;

export function pickRefundRecordScalar(
  record: Record<string, unknown> | null | undefined,
  keys: readonly string[],
): unknown {
  if (!record) return undefined;
  for (const key of keys) {
    if (key in record && record[key] != null && record[key] !== "") {
      return record[key];
    }
  }
  return undefined;
}

/** Ride start date for refund list table / export (`formatRefundDateTime` rules). */
export function formatRefundRideStartDate(
  record: Record<string, unknown> | null | undefined,
): string {
  const raw = pickRefundRecordScalar(record, REFUND_RIDE_START_DATE_KEYS);
  return raw != null && String(raw).trim() !== ""
    ? formatRefundDateTime(String(raw))
    : "";
}

/** Ride end / completion date for refund list table / export. */
export function formatRefundRideEndDate(
  record: Record<string, unknown> | null | undefined,
): string {
  const raw = pickRefundRecordScalar(record, REFUND_RIDE_END_DATE_KEYS);
  return raw != null && String(raw).trim() !== ""
    ? formatRefundDateTime(String(raw))
    : "";
}
